import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  UserGroupIcon, 
  TagIcon, 
  ClockIcon, 
  ArrowPathIcon,
  BanknotesIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const MembershipSettings = ({ settings, onSettingsChange, onHasChanges }) => {
  const [membershipSettings, setMembershipSettings] = useState({
    defaultPlan: '',
    gracePeriod: 3,
    autoRenewal: false,
    proRatedRefunds: true
  });

  const [availablePlans] = useState([
    { id: 'basic', name: 'Abonnement Basique', price: 2500, duration: 30 },
    { id: 'premium', name: 'Abonnement Premium', price: 4500, duration: 30 },
    { id: 'vip', name: 'Abonnement VIP', price: 8000, duration: 30 },
    { id: 'student', name: 'Abonnement Étudiant', price: 1800, duration: 30 },
    { id: 'annual', name: 'Abonnement Annuel', price: 25000, duration: 365 }
  ]);

  useEffect(() => {
    if (settings?.membership) {
      setMembershipSettings(settings.membership);
    }
  }, [settings]);

  const handleChange = (field, value) => {
    const newMembershipSettings = {
      ...membershipSettings,
      [field]: value
    };
    setMembershipSettings(newMembershipSettings);
    onSettingsChange({ ...settings, membership: newMembershipSettings });
    onHasChanges(true);
  };

  const getSelectedPlan = () => {
    return availablePlans.find(plan => plan.id === membershipSettings.defaultPlan);
  };

  const calculateGracePeriodEnd = () => {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + membershipSettings.gracePeriod);
    return endDate.toLocaleDateString('fr-FR');
  };

  const calculateProRatedRefund = (originalPrice, daysUsed, totalDays) => {
    const dailyRate = originalPrice / totalDays;
    const refund = originalPrice - (dailyRate * daysUsed);
    return Math.max(0, refund);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <UserGroupIcon className="h-6 w-6 text-blue-400" />
        <div>
          <h3 className="text-lg font-semibold text-white">Paramètres d'adhésion</h3>
          <p className="text-white/60 text-sm">Configuration des abonnements et conditions d'adhésion</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Plan par défaut */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-4"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-3 mb-4">
              <TagIcon className="h-5 w-5 text-purple-400" />
              <h4 className="font-semibold text-white">Plan par défaut</h4>
            </div>
            
            <div className="space-y-3">
              <select
                value={membershipSettings.defaultPlan}
                onChange={(e) => handleChange('defaultPlan', e.target.value)}
                className="w-full bg-white/10 backdrop-blur-lg rounded-xl px-4 py-3 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="" className="bg-gray-800 text-white">Aucun plan sélectionné</option>
                {availablePlans.map(plan => (
                  <option key={plan.id} value={plan.id} className="bg-gray-800 text-white">
                    {plan.name} - {plan.price} DA / {plan.duration} jours
                  </option>
                ))}
              </select>
              
              {getSelectedPlan() && (
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-white/60 text-sm mb-2">Plan sélectionné :</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/80">Nom :</span>
                      <span className="text-white">{getSelectedPlan().name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">Prix :</span>
                      <span className="text-white">{getSelectedPlan().price} DA</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">Durée :</span>
                      <span className="text-white">{getSelectedPlan().duration} jours</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Période de grâce */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-3 mb-4">
              <ClockIcon className="h-5 w-5 text-orange-400" />
              <h4 className="font-semibold text-white">Période de grâce</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={membershipSettings.gracePeriod}
                  onChange={(e) => handleChange('gracePeriod', parseInt(e.target.value) || 0)}
                  min="0"
                  max="30"
                  className="w-20 bg-white/10 backdrop-blur-lg rounded-xl px-3 py-2 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                />
                <span className="text-white/80">jours</span>
              </div>
              
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-white/60 text-sm mb-2">Exemple :</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/80">Expiration :</span>
                    <span className="text-white">01/01/2024</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/80">Accès jusqu'au :</span>
                    <span className="text-white">{calculateGracePeriodEnd()}</span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-sm">
                {[0, 3, 7].map(days => (
                  <button
                    key={days}
                    onClick={() => handleChange('gracePeriod', days)}
                    className={`p-2 rounded-lg transition-colors ${
                      membershipSettings.gracePeriod === days 
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'bg-white/5 text-white/80 hover:bg-white/10'
                    }`}
                  >
                    {days === 0 ? 'Aucune' : `${days} jours`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Renouvellement automatique */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-3 mb-4">
              <ArrowPathIcon className="h-5 w-5 text-green-400" />
              <h4 className="font-semibold text-white">Renouvellement automatique</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm">Activer le renouvellement automatique</p>
                  <p className="text-white/60 text-xs">Les abonnements se renouvellent automatiquement</p>
                </div>
                <button
                  onClick={() => handleChange('autoRenewal', !membershipSettings.autoRenewal)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    membershipSettings.autoRenewal ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      membershipSettings.autoRenewal ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <div className={`bg-white/5 rounded-xl p-3 ${
                membershipSettings.autoRenewal ? 'border border-green-500/20' : 'border border-red-500/20'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {membershipSettings.autoRenewal ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-400" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 text-red-400" />
                  )}
                  <p className="text-white/80 text-sm font-medium">
                    {membershipSettings.autoRenewal ? 'Activé' : 'Désactivé'}
                  </p>
                </div>
                <p className="text-white/60 text-xs">
                  {membershipSettings.autoRenewal 
                    ? 'Les membres seront automatiquement facturés à l\'expiration'
                    : 'Les membres devront renouveler manuellement leur abonnement'
                  }
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Remboursements proportionnels */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-4"
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-3 mb-4">
              <BanknotesIcon className="h-5 w-5 text-yellow-400" />
              <h4 className="font-semibold text-white">Remboursements proportionnels</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm">Activer les remboursements proportionnels</p>
                  <p className="text-white/60 text-xs">Calculer les remboursements selon la durée utilisée</p>
                </div>
                <button
                  onClick={() => handleChange('proRatedRefunds', !membershipSettings.proRatedRefunds)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    membershipSettings.proRatedRefunds ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      membershipSettings.proRatedRefunds ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-white/60 text-sm mb-2">Exemple de calcul :</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/80">Abonnement :</span>
                    <span className="text-white">3000 DA / 30 jours</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/80">Utilisé :</span>
                    <span className="text-white">10 jours</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t border-white/20 pt-1">
                    <span className="text-white">Remboursement :</span>
                    <span className="text-white">
                      {membershipSettings.proRatedRefunds 
                        ? `${calculateProRatedRefund(3000, 10, 30).toFixed(0)} DA`
                        : '0 DA'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Résumé des paramètres */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10"
      >
        <h3 className="text-lg font-semibold text-white mb-4">Résumé des paramètres d'adhésion</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <TagIcon className="h-6 w-6 text-purple-400 mx-auto mb-2" />
            <div className="text-sm font-bold text-white">
              {getSelectedPlan()?.name || 'Aucun plan'}
            </div>
            <div className="text-white/60 text-xs">Plan par défaut</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <ClockIcon className="h-6 w-6 text-orange-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">{membershipSettings.gracePeriod}</div>
            <div className="text-white/60 text-xs">Jours de grâce</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <ArrowPathIcon className="h-6 w-6 text-green-400 mx-auto mb-2" />
            <div className="text-sm font-bold text-white">
              {membershipSettings.autoRenewal ? 'Activé' : 'Désactivé'}
            </div>
            <div className="text-white/60 text-xs">Renouvellement auto</div>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <BanknotesIcon className="h-6 w-6 text-yellow-400 mx-auto mb-2" />
            <div className="text-sm font-bold text-white">
              {membershipSettings.proRatedRefunds ? 'Activé' : 'Désactivé'}
            </div>
            <div className="text-white/60 text-xs">Remboursements</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default MembershipSettings; 